import type { ContactJson } from 'app/types/entities'

/**
 * The unified SMS-template merge-token vocabulary. Canonical grammar is
 * namespaced dotted paths ({{contact.firstName}}); the three pre-existing
 * engines' flat keys are accepted as aliases (LEGACY_ALIASES) so nothing
 * breaks mid-migration. See docs/superpowers/specs/2026-08-07-sms-templates-design.md.
 */
export type TemplateToken = {
  /** What goes between {{ and }} — e.g. "contact.firstName". */
  key: string
  /** Short human label shown on insert chips. */
  label: string
  /** One-line tooltip explaining what renders at send time. */
  description: string
  /** Value used by renderSample for editor previews. */
  sample: string
}

export const TEMPLATE_TOKENS: TemplateToken[] = [
  {
    key: `contact.firstName`,
    label: `First name`,
    description: `Contact's first name (e.g. "Mark").`,
    sample: `Mark`,
  },
  { key: `contact.lastName`, label: `Last name`, description: `Contact's last name.`, sample: `Hendrix` },
  { key: `contact.name`, label: `Full name`, description: `Contact's full name.`, sample: `Mark Hendrix` },
  { key: `contact.email`, label: `Email`, description: `Contact's email address.`, sample: `mark@example.com` },
  {
    key: `contact.cellPhone`,
    label: `Cell phone`,
    description: `Contact's cell phone number.`,
    sample: `(555) 010-1234`,
  },
  {
    key: `contact.leadOwner`,
    label: `Contact owner`,
    description: `Name of the staff member who owns the contact.`,
    sample: `Dr. Patel`,
  },
]

/**
 * Legacy flat keys from the pre-template engines (bulk SMS `first_name`,
 * builder `firstName`), accepted as aliases so existing message bodies and
 * muscle-memory typing keep resolving during the migration.
 */
export const LEGACY_ALIASES: Record<string, string> = {
  first_name: `contact.firstName`,
  firstName: `contact.firstName`,
  lastName: `contact.lastName`,
  name: `contact.name`,
  email: `contact.email`,
  cellPhone: `contact.cellPhone`,
  owner: `contact.leadOwner`,
  leadOwner: `contact.leadOwner`,
  // Shipped briefly as the canonical spelling on this branch before the API
  // settled on contact.leadOwner (trackable-api#478). Bodies already saved
  // with it keep resolving.
  'contact.owner': `contact.leadOwner`,
}

/**
 * Shared token grammar, matching what the engine parses (trackable-api
 * src/lib/aiWorkflowTemplate.ts): `{{contact.firstName}}` with an optional
 * nullish-coalescing fallback, `{{contact.firstName ?? "there"}}`, whose
 * literal is single- or double-quoted.
 *
 * Groups: 1 = key, 2 = separator, 3 = raw fallback (quotes still attached for
 * the `??` form). Pass 2 and 3 through `parseTokenFallback` rather than reading
 * group 3 directly, so every consumer unquotes identically.
 *
 * `|fallback` stays accepted as a legacy separator: this branch emitted it
 * before the engine's `??` grammar shipped, so it can be sitting in saved
 * template bodies and workflow nodes.
 *
 * Known limitation, shared with the engine: braces cannot appear inside a
 * `{{ }}` expression, since they collide with the delimiters. A fallback
 * containing one breaks the match and the raw text passes through untouched.
 * The editors never author braces; hand-typing one is out of contract.
 */
export const TOKEN_PATTERN = /\{\{\s*([\w.]+)\s*(?:(\?\?|\|)\s*([^}]*?))?\s*\}\}/g

/**
 * The usable fallback text from a TOKEN_PATTERN match, or undefined when the
 * token has none. For the engine's `??` form this strips the surrounding
 * quotes and unescapes `\"`, `\'` and `\\`; the legacy `|` form is bare text.
 * Blank counts as absent, so an empty literal falls through to the caller's
 * unresolved policy instead of rendering nothing.
 */
export function parseTokenFallback(separator: string | undefined, raw: string | undefined): string | undefined {
  if (!separator || raw == null) return undefined
  const trimmed = raw.trim()
  if (separator === `|`) return trimmed || undefined
  const quoted = /^(["'])([\s\S]*)\1$/.exec(trimmed)
  // An unquoted `??` literal is malformed per the engine's grammar. Take it as
  // written anyway: dropping it would silently lose the author's intent.
  const inner = quoted ? quoted[2] : trimmed
  const unescaped = inner.replace(/\\(["'\\])/g, `$1`)
  return unescaped || undefined
}

/**
 * Render `text` as a `??` fallback literal for the engine: escape backslashes
 * and double quotes, and drop braces, which the engine cannot parse inside an
 * expression at all.
 */
function toFallbackLiteral(text: string): string {
  return text.replace(/\\/g, `\\\\`).replace(/"/g, `\\"`).replace(/[{}]/g, ``)
}

export type TemplateContext = { contact?: ContactJson }

export type SubstitutionResult = { text: string; unresolved: string[] }

function canonicalKey(raw: string): string {
  return LEGACY_ALIASES[raw] ?? raw
}

function resolveToken(key: string, context: TemplateContext): string | undefined {
  const contact = context.contact
  if (!contact) return undefined
  switch (key) {
    case `contact.firstName`:
      // Contact.firstName can be unpopulated on older records — fall back to
      // splitting the full name, matching the bulk-SMS resolver's behavior.
      return contact.firstName || (contact.name ?? ``).trim().split(/\s+/)[0] || undefined
    case `contact.lastName`:
      return contact.lastName || undefined
    case `contact.name`:
      return contact.name || undefined
    case `contact.email`:
      return contact.email || undefined
    case `contact.cellPhone`:
      return contact.cellPhone || undefined
    case `contact.leadOwner`:
      return contact.leadOwner?.name || undefined
    default:
      return undefined
  }
}

/**
 * Fill {{tokens}} (canonical or legacy-aliased, optionally piped as
 * {{key|fallback}}) from the context. Returns the rendered text plus the
 * canonical keys that could not be resolved. Resolution order: the real
 * value, then `opts.fallbacks` (checked under the canonical key first, then
 * the raw key as typed — legacy callers key fallbacks by flat key), then the
 * inline `|fallback` (trimmed; empty counts as none), then policy. Unresolved
 * policy belongs to the caller: `keepUnresolved` leaves the whole token
 * (including its pipe) literal (composer surfaces where a human reviews the
 * final text before sending); the default strips them (real automated sends,
 * where a typo'd token must never be delivered as literal text).
 */
export function substituteTokens(
  body: string,
  context: TemplateContext,
  opts?: { keepUnresolved?: boolean; fallbacks?: Record<string, string> },
): SubstitutionResult {
  const unresolved: string[] = []
  const text = body.replace(
    TOKEN_PATTERN,
    (whole, raw: string, separator: string | undefined, rawFallback: string | undefined) => {
      const key = canonicalKey(raw)
      const value = resolveToken(key, context)
      if (value != null) return value
      const mapFallback = opts?.fallbacks?.[key] ?? opts?.fallbacks?.[raw]
      if (mapFallback) return mapFallback
      const inline = parseTokenFallback(separator, rawFallback)
      if (inline) return inline
      unresolved.push(key)
      return opts?.keepUnresolved ? whole : ``
    },
  )
  return { text, unresolved }
}

/** The canonicalized keys of every token present in the text, in order. */
export function extractTokenKeys(body: string): string[] {
  // matchAll clones the regex but inherits lastIndex — reset so an external
  // .test()/.exec() on the exported pattern can never make us start mid-string.
  TOKEN_PATTERN.lastIndex = 0
  const keys: string[] = []
  for (const match of body.matchAll(TOKEN_PATTERN)) {
    keys.push(canonicalKey(match[1]))
  }
  return keys
}

/** Editor preview: fill known tokens with sample values; unknown ones fall
 *  back to their inline fallback text, or show as [key]. */
export function renderSample(body: string): string {
  return body.replace(
    TOKEN_PATTERN,
    (_whole, raw: string, separator: string | undefined, rawFallback: string | undefined) => {
      const key = canonicalKey(raw)
      const sample = TEMPLATE_TOKENS.find((t) => t.key === key)?.sample
      if (sample) return sample
      return parseTokenFallback(separator, rawFallback) || `[${raw}]`
    },
  )
}

/**
 * Prepare a template body for storage in a workflow node, where the engine
 * resolves the tokens at run time rather than us resolving them now.
 *
 * Since trackable-api#478 the engine speaks this same canonical vocabulary
 * ({{contact.firstName}}, not {{firstName}}), so keys pass straight through.
 * Only two things change: legacy aliases are normalized to their canonical
 * spelling, and fallbacks are materialized into the engine's grammar,
 * `{{contact.firstName ?? "there"}}`.
 *
 * The structured `fallbacks` map (keyed by canonical token) wins over a
 * fallback typed inline for the same key. `||` rather than `??` on that lookup
 * is deliberate: an empty structured entry falls through to the inline one,
 * matching substituteTokens' falsy-skips semantics.
 */
export function toEngineText(body: string, fallbacks?: Record<string, string>): string {
  return body.replace(
    TOKEN_PATTERN,
    (_whole, raw: string, separator: string | undefined, rawFallback: string | undefined) => {
      const key = canonicalKey(raw)
      const fallback = fallbacks?.[key] || parseTokenFallback(separator, rawFallback)
      return fallback ? `{{${key} ?? "${toFallbackLiteral(fallback)}"}}` : `{{${key}}}`
    },
  )
}
